import { useCallback, useEffect, useMemo } from 'react';
import { useSimpleSearchJQL, useFilteredViewContextInput } from '@atlassian/jira-business-filters';
import {
	type ChildrenQueryData,
	type ExpandedItems,
	useUpdateIssueParent,
	useHierarchyCache,
} from '@atlassian/jira-business-list-handlers';
import { ff } from '@atlassian/jira-feature-flagging';
import { useFlagService, type FlagConfiguration } from '@atlassian/jira-flags';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
	fireTrackAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useIsLinkingEnabled } from '@atlassian/jira-router-resources-business-configuration';
import { useOverview } from '@atlassian/jira-router-resources-business-overview-details';
import type { OnItemDrop } from '@atlassian/jira-software-roadmap-timeline-table/src/types';
import { fieldIds } from '../../common/constants';
import { useIssueParentMap } from '../../common/utils/use-issue-parent-map';
import { useIssuesMap } from '../../common/utils/use-issues-map';
import { useTimelineViewIssues as useTimelineViewIssuesService } from '../../services/get-timeline-view-issues';
import type {
	JWMTimelineViewItems,
	JWMTimelineViewItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as JWMTimelineViewItemsNode,
} from '../../services/get-timeline-view-issues/__generated_apollo__/JWMTimelineViewItems';
import { TIMELINE_VIEW_ITEMS_QUERY } from '../../services/get-timeline-view-issues/gql';
import type {
	UpdateInCache,
	UpdateItemsInCache,
	RankIssueArg,
	TimelineViewIssuesReturn,
} from '../../services/get-timeline-view-issues/types';
import { addChildToParentTimeline } from '../../services/get-timeline-view-issues/utils';
import { useCreateItemAnchor } from '../create-item-anchor';
import messages from './messages';
import type { TimelineIssuesReturn, OnItemDropRankIssue } from './types';
import { getReparentAction } from './utils';
import { toTimelineData } from './utils/to-timeline-data';

export const LOADING_ITEMS_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.loadingItemsErrorTitle,
	description: messages.loadingItemsErrorDescription,
};

const RANK_ERROR_FLAG: FlagConfiguration = {
	type: 'error',
	title: messages.rankingErrorTitle,
	description: messages.rankingErrorMessage,
};

// it can't be called from multiple places with the same query key and filter, otherwise it clears Apollo cache
// @ts-expect-error - TS2741 - Property 'expandedItems' is missing in type '{}' but required in type '{ childrenQueryData?: ChildrenQueryData<TimelineViewIssuesReturn> | undefined; expandedItems: Partial<Record<string, boolean>>; }'.
export const useTimelineViewIssues = ({
	childrenQueryData = {},
	expandedItems,
}: {
	childrenQueryData?: ChildrenQueryData<TimelineViewIssuesReturn>;
	expandedItems: ExpandedItems;
} = {}): TimelineIssuesReturn => {
	const { hierarchyLevelsWithCache } = useHierarchyCache({
		view: 'timeline',
	});

	const hierarchyLevels = useMemo(() => {
		if (ff('jwm.visual-hierarchy-milestone-1')) {
			return hierarchyLevelsWithCache;
		}
		return undefined;
	}, [hierarchyLevelsWithCache]);

	const isLinkingEnabled = useIsLinkingEnabled();
	const [{ issuesMap }] = useIssuesMap();
	const [{ issueParentMap }] = useIssueParentMap();
	const { showFlag } = useFlagService();
	const [{ createItemAnchor }] = useCreateItemAnchor();
	const { quickFiltersJql, hierarchyParent, isFiltering, jqlContext } = useFilteredViewContextInput(
		{
			view: 'timeline',
		},
	);
	const { isSearching } = useSimpleSearchJQL();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: overview } = useOverview();
	const isOverview = overview != null && Array.isArray(overview.projects);

	const {
		data: timelineViewIssuesData,
		loadNextPage,
		loading: issuesLoading,
		isLoadingNextPage,
		updateInCache: updateInCacheTopLevel,
		rankIssue: rankIssueTopLevel,
		updateItemsInCache: updateItemsInCacheTopLevel,
		error,
	} = useTimelineViewIssuesService({
		input: {
			jqlContext,
			fieldIds,
			quickFiltersJql,
			hierarchyParent,
			...(ff('jwm.visual-hierarchy-milestone-1') &&
				hierarchyLevels !== undefined && {
					// the above check will be unneccessary once the FF is removed
					hierarchyLevel: hierarchyLevels[0]?.level,
				}),
		},
		onComplete: () => {
			if (isSearching) {
				fireOperationalAnalytics(createAnalyticsEvent({}), 'timelineSimpleSearchQuery succeeded');
			}
		},
	});

	const { updateIssueParent: reparentIssue, unlinkParentIssue } = useUpdateIssueParent<
		TimelineViewIssuesReturn,
		JWMTimelineViewItemsNode,
		JWMTimelineViewItems
	>({
		childrenQueryData,
		fieldIds,
		issueParentMap,
		issuesMap,
		cacheQuery: TIMELINE_VIEW_ITEMS_QUERY,
		addChildToParent: addChildToParentTimeline<JWMTimelineViewItemsNode>,
		view: 'timeline',
		packageName: 'jiraBusinessTimeline',
	});

	useEffect(() => {
		if (error) {
			showFlag(LOADING_ITEMS_ERROR_FLAG);
		}
	}, [showFlag, error]);

	const data = toTimelineData({
		timelineViewIssuesData,
		childrenQueryData,
		expandedItems,
		createItemAnchor,
		issuesMap,
		issueParentMap,
		isLinkingEnabled,
		isFiltering,
		...(ff('jwm.visual-hierarchy-milestone-1') ? { isOverview } : {}),
	});

	const updateInCache: UpdateInCache = useCallback(
		(params) => {
			const updatedIssueParentIssueId = issueParentMap.get(params.id);

			// if the updated issue is parent level (no parent found), we just update the parent
			if (updatedIssueParentIssueId === undefined) {
				updateInCacheTopLevel(params);
				return;
			}

			// otherwise if the updated issue is a children issue
			// get the correct callback from the specific children query
			if (childrenQueryData[updatedIssueParentIssueId]) {
				childrenQueryData[updatedIssueParentIssueId].updateInCache(params);
			}
		},
		[childrenQueryData, issueParentMap, updateInCacheTopLevel],
	);

	/*
        Using this method you can only update many issues from the top level or
        many children of the same parent.
    */
	const updateItemsInCache: UpdateItemsInCache = useCallback(
		(params) => {
			const firstIssue = params[0];
			const updatedIssueParentIssueId = issueParentMap.get(firstIssue.id);

			// if one of the issues is parent level (no parent found)
			if (updatedIssueParentIssueId === undefined) {
				updateItemsInCacheTopLevel(params);
				return;
			}

			// otherwise if the updated issue is a children issue
			// get the correct callback from the specific children query
			if (childrenQueryData[updatedIssueParentIssueId]) {
				childrenQueryData[updatedIssueParentIssueId].updateItemsInCache(params);
			}
		},
		[childrenQueryData, issueParentMap, updateItemsInCacheTopLevel],
	);

	const rankIssue: OnItemDropRankIssue = useCallback(
		(id, { rankRequest }, event, skipUpdateCache) => {
			if (!rankRequest) {
				fireTrackAnalytics(event, 'rankIssue invalidRequest');
				return;
			}

			const updatedIssueParentIssueId = issueParentMap.get(id);
			// @ts-expect-error - TS2339 - Property 'rankBeforeId' does not exist on type 'RankRequest'.
			const relativeIssueId = rankRequest.rankAfterId || rankRequest.rankBeforeId;
			const relativeIssue = issuesMap.get(relativeIssueId);
			if (!relativeIssue) {
				fireTrackAnalytics(event, 'rankIssue invalidRequest', {
					relativeIssue: relativeIssueId,
				});
				showFlag(RANK_ERROR_FLAG);
				return;
			}

			const params: RankIssueArg = {
				issueId: id,
				relativeIssue,
				skipUpdateCache,
				// @ts-expect-error - TS2339 - Property 'rankBeforeId' does not exist on type 'RankRequest'.
				rankCall: rankRequest.rankBeforeId ? 'rankBefore' : 'rankAfter',
			};

			if (updatedIssueParentIssueId === undefined) {
				rankIssueTopLevel(params);
				return;
			}

			// get the correct callback from the specific children query
			if (childrenQueryData[updatedIssueParentIssueId]) {
				childrenQueryData[updatedIssueParentIssueId].rankIssue(params);
			}
		},
		[issueParentMap, issuesMap, childrenQueryData, showFlag, rankIssueTopLevel],
	);

	const updateIssueParent: OnItemDrop = useCallback(
		async (id, payload, event) => {
			const { parentId, rankRequest, level } = payload;
			const parentIssue = parentId ? issuesMap.get(parentId) : undefined;
			const parentIdOld = issueParentMap.get(id);

			if (!parentId) {
				await unlinkParentIssue({ issueKey: id, rankRequest });
			} else {
				if (parentIdOld === parentId) {
					return;
				}
				if (!parentIssue) {
					fireTrackAnalytics(event, 'reparent invalidRequest', {
						parentId,
					});
					return;
				}
				await reparentIssue({
					issueKey: id,
					parentIssue,
					rankRequest,
				});
			}

			fireUIAnalytics(createAnalyticsEvent({}), 'timelineDragAndDrop reparent', {
				action: getReparentAction(parentId, parentIdOld),
				isRanking: !!rankRequest,
				hierarchyLevel: level,
				parentHierarchyLevel: parentIssue?.issueType?.hierarchyLevel,
			});

			// Re-rank the issue if a rank request is provided during the reparenting process
			if (rankRequest) {
				rankIssue(id, payload, event, true);
			}
		},
		[createAnalyticsEvent, issueParentMap, issuesMap, rankIssue, reparentIssue, unlinkParentIssue],
	);

	return {
		loading: issuesLoading,
		error,
		data,
		loadNextPage,
		isLoadingNextPage,
		updateInCache,
		rankIssue,
		updateItemsInCache,
		isLinkingEnabled,
		updateIssueParent,
	};
};
